// NotificationContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import Pusher from "pusher-js";

const PUSHER_KEY = "540d521fe5fe005cb99d";
const PUSHER_CLUSTER = "ap2";

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children, user }) => {
  const [unreadChat, setUnreadChat] = useState(0);
  const [unreadTasks, setUnreadTasks] = useState(0);
  const [unreadLeaves, setUnreadLeaves] = useState(0); // ✅ NEW: Leave requests count
  const soundRef = useRef(null);
  const audioUnlocked = useRef(false);
  const pusherInstanceRef = useRef(null);
  const channelsRef = useRef({});

  // Unlock audio after the first user click (browser policy)
  useEffect(() => {
    const unlockAudio = () => {
      if (!audioUnlocked.current) {
        const audio = new Audio("/notification.mp3");
        audio.play().then(() => {
          audio.pause();
          audio.currentTime = 0;
          audioUnlocked.current = true;
          soundRef.current = audio;
        }).catch(() => {});
        document.removeEventListener("click", unlockAudio);
      }
    };
    document.addEventListener("click", unlockAudio);
    return () => document.removeEventListener("click", unlockAudio);
  }, []);

  const playSound = () => {
    try {
      if (soundRef.current && audioUnlocked.current) {
        soundRef.current.currentTime = 0;
        soundRef.current.play().catch(() => {});
      }
    } catch (error) {
      console.error("Sound play error:", error);
    }
  };

  useEffect(() => {
    // ✅ Only initialize if user exists
    if (!user || !user.id) {
      return;
    }

    // ✅ Clean up any existing Pusher instance
    if (pusherInstanceRef.current) {
      try {
        pusherInstanceRef.current.disconnect();
      } catch (e) {
        console.error("Cleanup error:", e);
      }
      pusherInstanceRef.current = null;
    }

    let pusher = null;
    let chatChannel = null;
    let userChannel = null;

    try {
      // ✅ Create Pusher instance with minimal options
      pusher = new Pusher(PUSHER_KEY, {
        cluster: PUSHER_CLUSTER,
        forceTLS: true,
        enabledTransports: ['ws', 'wss'],
      });

      pusherInstanceRef.current = pusher;

      // ✅ Handle connection states
      pusher.connection.bind('error', (err) => {
        console.warn('Pusher connection error:', err?.message || err);
      });

      pusher.connection.bind('connected', () => {
        console.log('Pusher connected');
      });

      pusher.connection.bind('disconnected', () => {
        console.log('Pusher disconnected');
      });

      // ✅ Subscribe to global chat channel
      chatChannel = pusher.subscribe("chat");
      channelsRef.current.chat = chatChannel;

      chatChannel.bind("new-message", (data) => {
        try {
          if (data && data.receiver_id && String(data.receiver_id) === String(user.id) && window.location.pathname !== "/chat") {
            setUnreadChat((prev) => prev + 1);
            playSound();
          }
        } catch (err) {
          console.error("new-message handler error:", err);
        }
      });

      // ✅ Subscribe to user-specific channel
      const userChannelName = `user-${user.id}`;
      userChannel = pusher.subscribe(userChannelName);
      channelsRef.current.user = userChannel;

      userChannel.bind("new-task", (data) => {
        try {
          if (window.location.pathname !== "/my-tasks") {
            setUnreadTasks((prev) => prev + 1);
            playSound();
          }
        } catch (err) {
          console.error("new-task handler error:", err);
        }
      });

      // ✅ NEW: Listen for new leave requests (admin only)
      userChannel.bind("new-leave-request", (data) => {
        try {
          if (user.role === 'admin') {
            setUnreadLeaves((prev) => prev + 1);
            playSound();
            console.log("New leave request received:", data);
          }
        } catch (err) {
          console.error("new-leave-request handler error:", err);
        }
      });

      // ✅ NEW: Listen for leave status updates (member only)
      userChannel.bind("leave-status-update", (data) => {
        try {
          playSound();
          console.log("Leave status update:", data);
        } catch (err) {
          console.error("leave-status-update handler error:", err);
        }
      });

      console.log("Pusher initialized for user:", user.id);

    } catch (error) {
      console.error("Pusher initialization error:", error);
    }

    // ✅ Cleanup function
    return () => {
      try {
        if (chatChannel) {
          chatChannel.unbind_all();
          pusher?.unsubscribe("chat");
        }
        if (userChannel) {
          userChannel.unbind_all();
          pusher?.unsubscribe(`user-${user.id}`);
        }
        if (pusher) {
          pusher.disconnect();
        }
        pusherInstanceRef.current = null;
        channelsRef.current = {};
      } catch (error) {
        console.error("Pusher cleanup error:", error);
      }
    };
  }, [user?.id, user?.role]); // ✅ Added user.role dependency

  const resetChat = () => setUnreadChat(0);
  const resetTasks = () => setUnreadTasks(0);
  const resetLeaves = () => setUnreadLeaves(0); // ✅ NEW

  return (
    <NotificationContext.Provider value={{ 
      unreadChat, 
      unreadTasks, 
      unreadLeaves, // ✅ NEW
      resetChat, 
      resetTasks,
      resetLeaves // ✅ NEW
    }}>
      {children}
    </NotificationContext.Provider>
  );
};